import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * AI Batch Node Schema
 *
 * Submits large-scale AI inference requests via Anthropic/OpenAI batch APIs.
 * Connect a chat model sub-node (Anthropic, OpenAI, or Access LLM).
 *
 * Batch API limits:
 * - Anthropic: 10,000 requests, 32 MB max
 * - OpenAI: 50,000 requests, 200 MB max
 */
const schema: NodePropertySchema = {
  nodeType: 'ai-batch',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // INPUT DATA
    // ==================================================================
    {
      key: 'inputData',
      label: 'Input Data',
      type: 'string',
      placeholder: '{{ $node["NodeName"].array }}',
      description: 'Array of items to process. Each item will generate one AI request.',
      required: true
    },

    // ==================================================================
    // PROMPTS
    // ==================================================================
    {
      key: 'systemPrompt',
      label: 'System Prompt',
      type: 'text',
      typeOptions: {
        rows: 4
      },
      default: 'You are a helpful AI assistant.',
      placeholder: 'You are a helpful assistant that...',
      description: 'System instructions applied to all batch requests',
      required: false
    },

    {
      key: 'userPromptTemplate',
      label: 'User Prompt Template',
      type: 'text',
      typeOptions: {
        rows: 4,
        insertFieldMode: 'arrayItem',      // Custom mode for array item fields
        insertFieldSource: 'inputData'     // Look at inputData property for the array
      },
      placeholder: '{{ $node["NodeName"].item }}',
      description: 'Template for generating user prompts. Use .item to reference the current item in the array (e.g., {{ $node["Source"].output.data.item.field }}).',
      required: true
    },

    // ==================================================================
    // TEST MODE
    // ==================================================================
    {
      key: 'testMode',
      label: 'Test Mode',
      type: 'options',
      default: 'full',
      options: [
        {
          name: 'Full Batch',
          value: 'full',
          description: 'Submit all items as a batch job (workflow suspends)'
        },
        {
          name: 'Single Item Preview',
          value: 'preview',
          description: 'Process first item synchronously to test prompts'
        },
        {
          name: 'Dry Run',
          value: 'dryrun',
          description: 'Validate inputs and estimate tokens without API calls'
        },
        {
          name: 'Sample Batch (5 items)',
          value: 'sample',
          description: 'Submit only first 5 items as a small test batch'
        }
      ],
      description: 'Use preview/dry run to test before submitting full batch',
      required: false
    },

    // ==================================================================
    // MODEL SETTINGS
    // ==================================================================
    {
      key: 'maxTokens',
      label: 'Max Tokens',
      type: 'number',
      default: 4096,
      typeOptions: {
        minValue: 100,
        maxValue: 200000
      },
      description: 'Maximum tokens per response',
      section: 'settings'
    },

    {
      key: 'temperature',
      label: 'Temperature',
      type: 'number',
      default: 0.7,
      typeOptions: {
        minValue: 0,
        maxValue: 2,
        numberPrecision: 2
      },
      description: 'Controls randomness (0 = deterministic, 1 = creative)',
      section: 'settings'
    },

    // ==================================================================
    // BATCH INFO (READ-ONLY DISPLAY)
    // ==================================================================
    {
      key: 'batchLimitsInfo',
      label: 'Batch API Limits',
      type: 'notice',
      typeOptions: {
        noticeType: 'info'
      },
      default: 'Anthropic: 10,000 requests (32 MB max) | OpenAI: 50,000 requests (200 MB max). Both providers offer 50% cost savings vs real-time API.',
      section: 'settings'
    }
  ],

  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://docs.evoe2e.com/nodes/ai-batch'
  }
};

export default schema;
